import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Accordion, AccordionVariant, } from '@thoughtspot/radiant-react/widgets/accordion';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { MutedAlert } from '@thoughtspot/radiant-react/widgets/alert/muted/muted-alert';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigPrePostFixStrings } from '../../settings/common';
import { FeatureManagementTranslationKeys } from '../constants';
import { EarlyAccessCard } from '../early-access-card';
import { FeatureCard } from '../feature-card';
import { ConfigureOrgsModal, } from './configure-orgs-modal/configure-orgs-modal';
import styles from './feature-management.module.scss';
import { FeatureAccessTab, getFeatureDisplayName, } from './feature-management-table.util';
const noop = () => { };
export const FeatureManagement = ({ loading = false, features = [], allOrgs = [], selectedTab = FeatureAccessTab.GeneralAccess, onSaveFeatureOrgs = noop, onSearch = noop, onToggleFeature = noop, }) => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    useEffect(() => {
        setSearchValue('');
        onSearch('');
    }, [selectedTab, onSearch]);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [selectedFeature, setSelectedFeature] = useState(null);
    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
        onSearch(value);
    }, [onSearch]);
    const handleManageOrgs = useCallback((featureId) => {
        const feature = features.find(f => f.id === featureId);
        if (feature) {
            setSelectedFeature(feature);
            setIsModalOpen(true);
        }
    }, [features]);
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedFeature(null);
    }, []);
    const selectedOrgIds = useMemo(() => {
        if (!selectedFeature || !selectedFeature.orgs)
            return [];
        return allOrgs
            .filter(org => selectedFeature.orgs.includes(org.name))
            .map(org => org.id);
    }, [selectedFeature, allOrgs]);
    const handleSaveFeature = useCallback((orgIds) => {
        if (selectedFeature) {
            const featureName = getFeatureDisplayName(selectedFeature, t);
            onSaveFeatureOrgs(selectedFeature.id, orgIds, () => {
                if (selectedTab === FeatureAccessTab.EarlyAccess &&
                    orgIds.length > 0) {
                    onToggleFeature(selectedFeature.id, true);
                }
                setToastMessage(t(FeatureManagementTranslationKeys.TOAST_ORGS_SETTINGS_UPDATED, { featureName }));
                setIsToastVisible(true);
            });
        }
        handleCloseModal();
    }, [
        selectedFeature,
        onSaveFeatureOrgs,
        handleCloseModal,
        t,
        selectedTab,
        onToggleFeature,
    ]);
    const handleDisableFeature = useCallback((featureId) => {
        const feature = features.find(f => f.id === featureId);
        onToggleFeature(featureId, false);
        onSaveFeatureOrgs(featureId, [], () => {
            if (feature) {
                setToastMessage(t(FeatureManagementTranslationKeys.TOAST_DISABLED_ALL_ORGS, { featureName: getFeatureDisplayName(feature, t) }));
                setIsToastVisible(true);
            }
        });
    }, [onToggleFeature, onSaveFeatureOrgs, features, t]);
    const categoryGroups = useMemo(() => {
        const groupMap = new Map();
        const order = [];
        features.forEach(feature => {
            const cat = feature.category || 'Other';
            if (!groupMap.has(cat)) {
                groupMap.set(cat, []);
                order.push(cat);
            }
            const group = groupMap.get(cat);
            if (group)
                group.push(feature);
        });
        return order.map(name => ({
            name,
            features: groupMap.get(name) || [],
        }));
    }, [features]);
    return (React.createElement("div", { className: styles.container },
        React.createElement(Horizontal, { vAlignContent: "center", spacing: "f", className: styles.toolbar },
            React.createElement("div", { className: styles.searchWrapper },
                React.createElement(SearchBar, { searchValue: searchValue, searchPlaceholder: t(FeatureManagementTranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: handleSearchChange, "data-testid": "feature-search" }))),
        React.createElement(LoadingIndicator.Global, { hideLoader: !loading, className: styles.loader },
            React.createElement("div", { className: styles.contentArea },
                features.length === 0 && !loading && (React.createElement("div", { className: styles.featureMgmtEmptyState },
                    React.createElement(MutedAlert, { global: true, imageVisible: true, errorCode: ErrorCode.GLOBAL_SEARCH, title: searchValue
                            ? t(FeatureManagementTranslationKeys.NO_SEARCH_RESULTS_TITLE)
                            : t(FeatureManagementTranslationKeys.NO_FEATURES_TITLE), description: searchValue
                            ? t(FeatureManagementTranslationKeys.NO_SEARCH_RESULTS_DESCRIPTION)
                            : t(FeatureManagementTranslationKeys.NO_FEATURES_DESCRIPTION) }))),
                features.length > 0 &&
                    selectedTab === FeatureAccessTab.EarlyAccess && (React.createElement("div", { className: styles.flatCardList }, features.map(feature => (React.createElement(EarlyAccessCard, { key: feature.id, feature: feature, onManageOrgs: handleManageOrgs, onDisableFeature: handleDisableFeature }))))),
                features.length > 0 &&
                    selectedTab !== FeatureAccessTab.EarlyAccess && (React.createElement("div", { className: styles.accordionWrapper },
                    React.createElement(Accordion, { expandMultipleItems: true, variant: AccordionVariant.LONG_LIST, useUncontrolledState: true, className: styles.accordion }, categoryGroups.map(group => (React.createElement(Accordion.Item, { key: group.name, header: t(`${ConfigPrePostFixStrings.TSCLI_CONFIG}.${group.name}.${ConfigPrePostFixStrings.TITLE}`), "data-testid": `category-${group.name}` },
                        React.createElement("div", { className: styles.featuresList }, group.features.map(feature => (React.createElement(FeatureCard, { key: feature.id, feature: feature, onManageOrgs: handleManageOrgs })))))))))))),
        React.createElement(ConfigureOrgsModal, { isOpen: isModalOpen, featureName: selectedFeature
                ? getFeatureDisplayName(selectedFeature, t)
                : '', featureDescription: selectedFeature?.description, learnMoreLink: selectedFeature?.docsUrl, orgs: allOrgs, selectedOrgIds: selectedOrgIds, isLoading: loading, hasExistingOrgs: !!(selectedFeature?.orgs && selectedFeature.orgs.length > 0), onDismiss: handleCloseModal, onSave: handleSaveFeature }),
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, onClose: () => {
                setIsToastVisible(false);
                setToastMessage('');
            }, autoHide: true, showCheckmarkIcon: true })));
};
//# sourceMappingURL=feature-management.js.map